import { z } from 'zod';

import { PageQuerySchema, SearchQuerySchema } from '../shared/pagination';
import { AgentToolSchema, SlugSchema } from './agents';
import { AgentModelTierSchema, LlmProviderSchema, ModelIdSchema } from './model-tiers';
import { CsDraftCategorySchema } from './outcomes';
import { MessageSchema } from './session/messages';

// Exact-evidence grader: resolve a bounded path over a named tool's JSON return and
// compare the resolved values against an expected multiset. `path` supports dot keys,
// array index (`[0]`), and array wildcard (`[*]`) segments, e.g.
// `products[*].catalog.itemId`. `allowExtra:false` requires exact cardinality — how
// `products[*].catalog.itemId = []` proves a genuinely empty result.
export const ToolJsonGraderSchema = z.object({
  toolJson: z.object({
    tool: AgentToolSchema,
    path: z.string().min(1),
    containsAll: z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
    allowExtra: z.boolean().default(true),
  }),
});
export type ToolJsonGrader = z.infer<typeof ToolJsonGraderSchema>;

// A grader is either a bare deterministic check, a keyed judge spec, or an exact
// tool-JSON evidence check.
export const GraderSchema = z.union([
  z.literal('tool-selection'),
  z.literal('answer-contains'),
  z.literal('draft-vs-cs'),
  z.object({ judge: z.object({ rubric: z.string().min(1), reference: z.string().optional() }) }),
  ToolJsonGraderSchema,
]);
export type Grader = z.infer<typeof GraderSchema>;

export const RecordedToolReturnSchema = z.object({
  toolName: z.string().min(1),
  content: z.json(),
  args: z.json().optional(),
});
export type RecordedToolReturn = z.infer<typeof RecordedToolReturnSchema>;

export const EvalReplaySchema = z.object({
  conversationId: z.string().min(1),
  category: z.string().min(1),
  draftedAt: z.iso.datetime(),
  inputs: z.array(z.string().min(1)).min(1),
  recorded: z.array(RecordedToolReturnSchema),
  gradeThread: z.string(),
  csReply: z.string().min(1),
});
export type EvalReplay = z.infer<typeof EvalReplaySchema>;

// The editable content of one task (no server-derived fields).
const evalTaskSpecShape = {
  taskKey: z.string().min(1).max(120),
  suite: z.enum(['capability', 'regression']),
  input: z.string().min(1),
  expectTools: z.array(AgentToolSchema).optional(),
  expectNoTools: z.boolean().optional(),
  expectAnswer: z.string().optional(),
  grade: z.array(GraderSchema).min(1),
  trials: z.number().int().min(1).default(1),
  passThreshold: z.number().int().min(1).default(1),
  replay: EvalReplaySchema.optional(),
};

// The three fail-loud invariants carried over verbatim from the harness's task
// schema. Inlined (not a generic helper) — only the write shape is refined.
export const EvalTaskSpecSchema = z
  .object(evalTaskSpecShape)
  .refine((t) => t.passThreshold <= t.trials, {
    message: 'passThreshold cannot exceed trials',
  })
  .refine((t) => !(t.grade.includes('answer-contains') && t.expectAnswer === undefined), {
    message: "grader 'answer-contains' requires expectAnswer",
  })
  .refine(
    (t) =>
      !(
        t.grade.includes('tool-selection') &&
        t.expectTools === undefined &&
        t.expectNoTools === undefined
      ),
    { message: "grader 'tool-selection' requires expectTools or expectNoTools" },
  )
  .refine((t) => !(t.grade.includes('draft-vs-cs') && t.replay === undefined), {
    message: "grader 'draft-vs-cs' requires replay",
  });
export type EvalTaskSpec = z.infer<typeof EvalTaskSpecSchema>;

// Persisted task = spec fields + server-derived identity/ordering. No refine on
// read: DB rows were validated on write.
export const EvalTaskSchema = z.object({
  ...evalTaskSpecShape,
  id: z.uuid(),
  suiteId: z.uuid(),
  position: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type EvalTask = z.infer<typeof EvalTaskSchema>;

const evalSuiteSpecShape = {
  name: SlugSchema,
  description: z.string().max(2000).optional(),
  // The suite is evaluated against this agent's currently-active version. Nullable:
  // a suite may be drafted unpinned, and the staging-only pin migration leaves
  // fresh/CI suites unpinned. A run against an unpinned suite fails fast.
  agentId: z.uuid().nullable(),
};
export const EvalSuiteSpecSchema = z.object(evalSuiteSpecShape);
export type EvalSuiteSpec = z.infer<typeof EvalSuiteSpecSchema>;

export const EvalSuiteSchema = z.object({
  ...evalSuiteSpecShape,
  id: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type EvalSuite = z.infer<typeof EvalSuiteSchema>;

export const EvalSuiteDetailSchema = EvalSuiteSchema.extend({
  tasks: z.array(EvalTaskSchema),
});
export type EvalSuiteDetail = z.infer<typeof EvalSuiteDetailSchema>;

export const ListEvalSuitesQuerySchema = PageQuerySchema.merge(SearchQuerySchema);
export type ListEvalSuitesQuery = z.infer<typeof ListEvalSuitesQuerySchema>;

export const EvalSuitePageSchema = z.object({
  items: z.array(EvalSuiteSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type EvalSuitePage = z.infer<typeof EvalSuitePageSchema>;

export const EVAL_RUN_STATUSES = ['running', 'completed', 'failed', 'canceled'] as const;
export const EvalRunStatusSchema = z.enum(EVAL_RUN_STATUSES);
export type EvalRunStatus = z.infer<typeof EvalRunStatusSchema>;

export const EVAL_RUN_SOURCES = ['ui', 'cli'] as const;
export const EVAL_RUN_GRADERS = ['draft-vs-cs', 'human-level'] as const;
export const EvalRunGraderSchema = z.enum(EVAL_RUN_GRADERS);
export type EvalRunGrader = z.infer<typeof EvalRunGraderSchema>;

export const CS_BASELINE_GRADER = 'cs-baseline';

export const EvalRunSourceSchema = z.enum(EVAL_RUN_SOURCES);
export type EvalRunSource = z.infer<typeof EvalRunSourceSchema>;

// One grader's verdict on a task (the harness's runtime GradeOutcome, now shared:
// persisted as eval_run_tasks.outcomes jsonb and read back through the API/UI).
export const GradeOutcomeSchema = z.object({
  grader: z.string(),
  pass: z.boolean(),
  reason: z.string(),
});
export type GradeOutcome = z.infer<typeof GradeOutcomeSchema>;

export const SuiteTallySchema = z.object({
  suite: z.enum(['capability', 'regression']),
  total: z.number().int(),
  passed: z.number().int(),
});
export type SuiteTally = z.infer<typeof SuiteTallySchema>;

export const CategoryTallySchema = z.object({
  category: z.string(),
  total: z.number().int(),
  passed: z.number().int(),
});
export type CategoryTally = z.infer<typeof CategoryTallySchema>;

export const EvalSummarySchema = z.object({
  total: z.number().int(),
  passed: z.number().int(),
  byTag: z.array(SuiteTallySchema),
  avgTokensIn: z.number(),
  avgTokensOut: z.number(),
  avgDurationMs: z.number(),
  byCategory: z.array(CategoryTallySchema).optional(),
  avgCostUsd: z.number().nullable().optional(),
});
export type EvalSummary = z.infer<typeof EvalSummarySchema>;

export const INVESTIGATOR_TOOL = 'agent.rca-agent';

export const EvalCandidateSchema = z.object({
  versionNumber: z.number().int().min(1).optional(),
  systemPrompt: z.string().min(1).optional(),
  modelTier: AgentModelTierSchema.optional(),
  removeTools: z.array(AgentToolSchema).optional(),
});
export type EvalCandidate = z.infer<typeof EvalCandidateSchema>;

export const EvalRunSchema = z.object({
  id: z.uuid(),
  suiteId: z.uuid().nullable(),
  suiteName: z.string(),
  status: EvalRunStatusSchema,
  source: EvalRunSourceSchema,
  // The provider that served the judge, frozen alongside its model id: without it a
  // replay of the run could not tell which adapter produced the verdicts.
  judgeProvider: LlmProviderSchema,
  judgeModel: ModelIdSchema,
  agentModel: ModelIdSchema,
  agentVersionId: z.uuid().nullable(),
  candidate: EvalCandidateSchema.nullable().default(null),
  label: z.string().nullable(),
  triggeredBy: z.uuid().nullable(),
  rerunOf: z.uuid().nullable().default(null),
  regradeOf: z.uuid().nullable().default(null),
  grader: EvalRunGraderSchema.nullable().default(null),
  skippedTasks: z.number().int().nonnegative().default(0),
  taskTotal: z.number().int().nonnegative(),
  taskDone: z.number().int().nonnegative(),
  summary: EvalSummarySchema.nullable(),
  error: z.string().nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type EvalRun = z.infer<typeof EvalRunSchema>;

export const EvalRunTaskSchema = z.object({
  id: z.uuid(),
  runId: z.uuid(),
  suiteName: z.string(),
  taskKey: z.string(),
  suiteTag: z.enum(['capability', 'regression']),
  passed: z.boolean(),
  outcomes: z.array(GradeOutcomeSchema),
  trials: z.object({ passed: z.number().int(), total: z.number().int() }).nullable(),
  tokensIn: z.number().int().nonnegative(),
  tokensOut: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  category: z.string().nullable().default(null),
  costUsd: z.number().nullable().default(null),
  transcript: z.array(MessageSchema),
  createdAt: z.iso.datetime(),
});
export type EvalRunTask = z.infer<typeof EvalRunTaskSchema>;

// Runs are listed newest-first and paginated; no free-text search (v1).
export const ListEvalRunsQuerySchema = PageQuerySchema;
export type ListEvalRunsQuery = z.infer<typeof ListEvalRunsQuerySchema>;

export const EvalRunPageSchema = z.object({
  items: z.array(EvalRunSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type EvalRunPage = z.infer<typeof EvalRunPageSchema>;

// Run + its per-task results so far — the poll response while status='running'.
export const EvalRunDetailSchema = EvalRunSchema.extend({
  tasks: z.array(EvalRunTaskSchema),
});
export type EvalRunDetail = z.infer<typeof EvalRunDetailSchema>;

export const TriggerEvalRunSchema = z.object({
  suiteId: z.uuid(),
  label: z.string().max(200).optional(),
  candidate: EvalCandidateSchema.optional(),
  rerunOf: z.uuid().optional(),
  regradeOf: z.uuid().optional(),
  taskKeys: z.array(z.string().min(1)).min(1).max(500).optional(),
  grader: EvalRunGraderSchema.optional(),
});
export type TriggerEvalRun = z.infer<typeof TriggerEvalRunSchema>;

export const BuildIntercomReplaySuiteSchema = z.object({
  suiteName: SlugSchema.default('intercom-drafter-replay'),
  agentId: z.uuid(),
  cases: z.number().int().min(1).max(500).default(200),
  lookbackDays: z.number().int().min(1).max(90).default(21),
  categories: z.array(CsDraftCategorySchema).min(1).optional(),
  draftedBefore: z.iso.datetime({ offset: true }).optional(),
});
export type BuildIntercomReplaySuite = z.infer<typeof BuildIntercomReplaySuiteSchema>;

export const IntercomReplaySuiteBuiltSchema = z.object({
  suiteId: z.uuid(),
  suiteName: z.string(),
  taskCount: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  byCategory: z.array(z.object({ category: z.string(), count: z.number().int() })),
});
export type IntercomReplaySuiteBuilt = z.infer<typeof IntercomReplaySuiteBuiltSchema>;
